import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAlert } from '../contexts/AlertContext';
import { useTranslation } from 'react-i18next';
import { resolveApiError } from '../utils/i18n';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ThemeToggle } from '@/components/ThemeToggle';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { apiService } from '../services/api';

// ponytail: 亞洲手機號碼驗證
const PHONE_REGEX = /^(\+852[5-8]\d{7}|\+81[789]0\d{8}|\+8869\d{8}|\+65[89]\d{7}|\+639\d{9}|\+628\d{8,11}|\+8210\d{8})$/;

import { COUNTRY_CODES } from '../constants/phoneCountryCodes';
// 常見緊急求助電話，不可作為本地電話號碼開頭
const EMERGENCY_PREFIXES = ['911', '112', '110', '119', '999', '000'];
const hasEmergencyPrefix = (localPhone: string) => EMERGENCY_PREFIXES.some(p => localPhone.startsWith(p));

// ponytail: same complexity rule as RegisterPage
const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{6,15}$/;

const getAccountSchema = (t: (key: string, fallback: string) => string) => z.object({
  username: z.string().min(3, t('settings.usernameMinLength', 'Username must be at least 3 characters')),
  email: z.string().email(t('settings.invalidEmail', 'Invalid email address')).optional().or(z.literal('')),
  preferredLanguage: z.string().optional(),
});

const getPasswordSchema = (t: (key: string, fallback: string) => string) => z.object({
  currentPassword: z.string().min(1, t('settings.currentPasswordRequired', 'Current password is required')),
  newPassword: z.string()
    .min(6, t('settings.passwordMinLength', 'Password must be at least 6 characters'))
    .regex(PASSWORD_REGEX, t('register.passwordRequirements', 'Password must be 6-15 characters with uppercase, lowercase, number, and symbol')),
  confirmPassword: z.string().min(1, t('settings.confirmPasswordRequired', 'Please confirm your password')),
}).refine(data => data.newPassword === data.confirmPassword, {
  message: t('settings.passwordMismatch', "Passwords don't match"),
  path: ["confirmPassword"],
});

type AccountFormValues = z.infer<ReturnType<typeof getAccountSchema>>;
type PasswordFormValues = z.infer<ReturnType<typeof getPasswordSchema>>;

const SettingsPage: React.FC = () => {
  const { user, updateProfile, refreshUser } = useAuth();
  const { showAlert } = useAlert();
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const activeSection = searchParams.get('tab') || 'account';
  const [saving, setSaving] = React.useState(false);

  // 'idle'：預設顯示；'verify'：驗證現有號碼（免輸入新號碼，直接發碼）；'change'：更改為新號碼
  const [waMode, setWaMode] = React.useState<'idle' | 'verify' | 'change'>('idle');
  const [waCountryCode, setWaCountryCode] = React.useState('+852');
  const [waLocalPhone, setWaLocalPhone] = React.useState('');
  const waNewPhone = `${waCountryCode}${waLocalPhone}`;
  const [waCodeSent, setWaCodeSent] = React.useState(false);
  const [waCode, setWaCode] = React.useState('');
  const [waLoading, setWaLoading] = React.useState(false);

  const accountSchema = useMemo(() => getAccountSchema(t as any), [t]);
  const passwordSchema = useMemo(() => getPasswordSchema(t as any), [t]);

  const accountForm = useForm<AccountFormValues>({
    resolver: zodResolver(accountSchema),
    defaultValues: {
      username: user?.username || '',
      email: user?.email || '',
      preferredLanguage: user?.preferredLanguage || i18n.language || 'zh-TW',
    }
  });

  const passwordForm = useForm<PasswordFormValues>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    }
  });

  const onAccountSubmit = async (data: AccountFormValues) => {
    try {
      setSaving(true);
      const response = await apiService.users.updateProfile(data as Partial<typeof user>);
      if (response.success && response.data) {
        await updateProfile(response.data);
        if (data.preferredLanguage) {
          i18n.changeLanguage(data.preferredLanguage);
        }
        accountForm.reset({
          username: response.data.username || '',
          email: response.data.email || '',
          preferredLanguage: response.data.preferredLanguage || data.preferredLanguage || i18n.language,
        });
        showAlert({ type: 'success', message: t('common.save') + ' ' + t('common.success', { defaultValue: 'Successfully' }) });
      }
    } catch (error: any) {
      showAlert({ type: 'error', message: resolveApiError(error?.response?.data?.errorCode, error?.response?.data?.message, t) });
    } finally {
      setSaving(false);
    }
  };

  const cancelWaEdit = () => {
    setWaMode('idle');
    setWaLocalPhone('');
    setWaCodeSent(false);
    setWaCode('');
  };

  const sendWaCode = async (phone: string) => {
    try {
      setWaLoading(true);
      const response = await apiService.users.requestWhatsappCode(phone);
      if (response.success) {
        setWaCodeSent(true);
        showAlert({ type: 'success', message: t('settings.waCodeSent', '驗證碼已發送，請於 10 分鐘內輸入驗證碼。') });
      }
    } catch (error: any) {
      showAlert({ type: 'warning', message: resolveApiError(error?.response?.data?.errorCode, error?.response?.data?.message, t) });
    } finally {
      setWaLoading(false);
    }
  };

  // 驗證現有號碼：不需輸入新號碼，按下即直接發碼
  const startWaVerify = () => {
    if (!user?.whatsappPhone) return;
    setWaMode('verify');
    setWaCodeSent(false);
    setWaCode('');
    sendWaCode(user.whatsappPhone);
  };

  // 更改為新號碼：先輸入新號碼再發碼
  const startWaChange = () => {
    setWaMode('change');
    setWaLocalPhone('');
    setWaCodeSent(false);
    setWaCode('');
  };

  const onRequestWhatsappCode = async () => {
    if (!waLocalPhone.trim()) return;
    if (hasEmergencyPrefix(waLocalPhone.trim())) {
      showAlert({ type: 'warning', message: t('settings.waEmergencyPrefix', '電話號碼不可以緊急求助電話號碼開頭') });
      return;
    }
    if (!PHONE_REGEX.test(waNewPhone.trim())) {
      showAlert({ type: 'warning', message: t('settings.waPhoneFormat', '請輸入正確的手提電話號碼（支援香港、日本、台灣、新加坡、菲律賓、印尼、韓國）') });
      return;
    }
    await sendWaCode(waNewPhone.trim());
  };

  const onVerifyWhatsappCode = async () => {
    if (!waCode.trim()) return;
    try {
      setWaLoading(true);
      const response = await apiService.users.verifyWhatsappCode(waCode.trim());
      if (response.success) {
        await refreshUser();
        showAlert({ type: 'success', message: waMode === 'verify' ? t('settings.waVerifiedOnly', '電話號碼已驗證') : t('settings.waUpdated', 'WhatsApp 電話號碼已更新成功') });
        cancelWaEdit();
      }
    } catch (error: any) {
      const errorCode = error?.response?.data?.errorCode;
      showAlert({ type: 'error', message: resolveApiError(errorCode, error?.response?.data?.message, t) });
      // 錯誤次數過多或已過期：強制回到重新發送的步驟
      if (errorCode === 'MAX_ATTEMPTS_EXCEEDED' || errorCode === 'CODE_EXPIRED') {
        setWaCodeSent(false);
        setWaCode('');
      }
    } finally {
      setWaLoading(false);
    }
  };

  const onPasswordSubmit = async (data: PasswordFormValues) => {
    try {
      setSaving(true);
      const response = await apiService.users.changePassword({
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
        confirmPassword: data.confirmPassword,
      });
      if (response.success) {
        showAlert({ type: 'success', message: t('common.save') + ' ' + t('common.success', { defaultValue: 'Successfully' }) });
        passwordForm.reset();
      }
    } catch (error: any) {
      showAlert({ type: 'error', message: resolveApiError(error?.response?.data?.errorCode, error?.response?.data?.message, t) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-xl">{t('settings.title')}</CardTitle>
          <p className="text-sm text-muted-foreground">{t('settings.description')}</p>
        </CardHeader>
      </Card>

      {activeSection === 'account' && (
            <Card className="border-border bg-card text-card-foreground">
              <CardHeader>
                <CardTitle>{t('settings.accountSettings')}</CardTitle>
                <CardDescription>{t('settings.accountDesc')}</CardDescription>
              </CardHeader>
                <CardContent>
                  <form onSubmit={accountForm.handleSubmit(onAccountSubmit)} className="space-y-6">
                    <div className="space-y-2">
                      <Label htmlFor="username">{t('settings.username', { defaultValue: '稱呼' })}</Label>
                      <p className="text-xs text-muted-foreground">
                        {t('settings.usernameDesc', { defaultValue: '此稱呼會在您發佈招聘或申請工作時對外公開顯示。' })}
                      </p>
                      <Input id="username" {...accountForm.register('username')} className="bg-background" />
                      {accountForm.formState.errors.username && (
                        <p className="text-sm bg-[#FFF0F0] text-[#872b57] rounded px-2 py-1">{accountForm.formState.errors.username.message}</p>
                      )}
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="email">{t('settings.email')}</Label>
                      <Input id="email" type="email" {...accountForm.register('email')} className="bg-background" />
                      {accountForm.formState.errors.email && (
                        <p className="text-sm bg-[#FFF0F0] text-[#872b57] rounded px-2 py-1">{accountForm.formState.errors.email.message}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="whatsappPhone">{t('settings.whatsappPhone')}</Label>
                      {waMode === 'idle' && (
                        <div className="flex items-center gap-2 flex-wrap">
                          <Input id="whatsappPhone" type="tel" value={user?.whatsappPhone || ''} disabled className="bg-muted flex-1" />
                          {user?.whatsappPhone && (
                            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap ${user.phoneVerified ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                              {user.phoneVerified ? t('settings.verified', '已驗證') : t('settings.notVerified', '未驗證')}
                            </span>
                          )}
                          {user?.whatsappPhone && !user.phoneVerified && (
                            <Button type="button" variant="outline" onClick={startWaVerify} disabled={waLoading}>
                              {waLoading ? t('settings.waSending', '發送中...') : t('settings.waVerifyBtn', '驗證')}
                            </Button>
                          )}
                          <Button type="button" variant="outline" onClick={startWaChange}>{t('settings.change', '更改')}</Button>
                        </div>
                      )}
                      {waMode === 'verify' && (
                        <div className="space-y-3 rounded-lg border border-border p-3">
                          {!waCodeSent ? (
                            <p className="text-sm text-muted-foreground">{t('settings.waSending', '發送中...')}</p>
                          ) : (
                            <>
                              <p className="text-xs text-muted-foreground">{t('settings.waCodeSentTo', '驗證碼已發送至您的電話，請於 10 分鐘內輸入。')}</p>
                              <div className="flex items-center gap-2">
                                <Input
                                  type="text"
                                  placeholder={t('settings.waCodePlaceholder', '輸入驗證碼')}
                                  value={waCode}
                                  onChange={(e) => setWaCode(e.target.value)}
                                  className="bg-background"
                                />
                                <Button type="button" disabled={waLoading || !waCode.trim()} onClick={onVerifyWhatsappCode}>
                                  {waLoading ? t('settings.waVerifying', '驗證中...') : t('settings.waVerify', '驗證')}
                                </Button>
                                <Button type="button" variant="ghost" onClick={() => { sendWaCode(user!.whatsappPhone); setWaCode(''); }}>
                                  {t('settings.waResend', '重新發送')}
                                </Button>
                                <Button type="button" variant="ghost" onClick={cancelWaEdit}>{t('common.cancel', '取消')}</Button>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                      {waMode === 'change' && (
                        <div className="space-y-3 rounded-lg border border-border p-3">
                          <p className="text-xs text-muted-foreground">
                            {t('settings.waSecurityNote', '為保障帳戶安全，更改電話號碼需重新驗證，且每 24 小時只可更改一次。')}
                          </p>
                          {!waCodeSent ? (
                            <div className="flex items-center gap-2">
                              <select
                                value={waCountryCode}
                                onChange={(e) => setWaCountryCode(e.target.value)}
                                className="h-9 w-28 rounded-md border border-input bg-background px-2 text-sm"
                              >
                                {COUNTRY_CODES.map(c => (
                                  <option key={c} value={c}>{c}</option>
                                ))}
                              </select>
                              <Input
                                type="tel"
                                placeholder={t('settings.waNewPhonePlaceholder', '輸入新的電話號碼')}
                                value={waLocalPhone}
                                onChange={(e) => setWaLocalPhone(e.target.value)}
                                className="bg-background"
                              />
                              <Button type="button" disabled={waLoading || !waLocalPhone.trim()} onClick={onRequestWhatsappCode}>
                                {waLoading ? t('settings.waSending', '發送中...') : t('settings.waSendCode', '發送驗證碼')}
                              </Button>
                              <Button type="button" variant="ghost" onClick={cancelWaEdit}>{t('common.cancel', '取消')}</Button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <Input
                                type="text"
                                placeholder={t('settings.waCodePlaceholder', '輸入驗證碼')}
                                value={waCode}
                                onChange={(e) => setWaCode(e.target.value)}
                                className="bg-background"
                              />
                              <Button type="button" disabled={waLoading || !waCode.trim()} onClick={onVerifyWhatsappCode}>
                                {waLoading ? t('settings.waVerifying', '驗證中...') : t('settings.waVerify', '驗證')}
                              </Button>
                              <Button type="button" variant="ghost" onClick={() => { setWaCodeSent(false); setWaCode(''); }}>
                                {t('settings.waResend', '重新發送')}
                              </Button>
                              <Button type="button" variant="ghost" onClick={cancelWaEdit}>{t('common.cancel', '取消')}</Button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="preferredLanguage">{t('settings.languagePref')}</Label>
                      <Controller
                        control={accountForm.control}
                        name="preferredLanguage"
                        render={({ field }) => (
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <SelectTrigger className="bg-background">
                              <SelectValue placeholder={t('settings.selectLanguage', 'Select Language')} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="en">{t('settings.languageEnglish', 'English')}</SelectItem>
                              <SelectItem value="zh-HK">{t('settings.languageZhHK', '繁體中文')}</SelectItem>
                              <SelectItem value="id">{t('settings.languageId', 'Bahasa Indonesia')}</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      />
                    </div>

                    <div className="pt-4 flex gap-4">
                      <Button type="submit" disabled={saving}>
                        {saving ? t('settings.saving') : t('settings.saveChanges')}
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            )}

      {activeSection === 'security' && (
            <Card className="border-border bg-card text-card-foreground">
              <CardHeader>
                <CardTitle>{t('settings.security')}</CardTitle>
                <CardDescription>{t('settings.securityDesc')}</CardDescription>
              </CardHeader>
                <CardContent>
                  <form onSubmit={passwordForm.handleSubmit(onPasswordSubmit)} className="space-y-6">
                    <div className="space-y-2">
                      <Label htmlFor="currentPassword">{t('settings.currentPassword')}</Label>
                      <Input id="currentPassword" type="password" {...passwordForm.register('currentPassword')} className="bg-background" />
                      {passwordForm.formState.errors.currentPassword && (
                        <p className="text-sm bg-[#FFF0F0] text-[#872b57] rounded px-2 py-1">{passwordForm.formState.errors.currentPassword.message}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="newPassword">{t('settings.newPassword')}</Label>
                      <Input id="newPassword" type="password" {...passwordForm.register('newPassword')} className="bg-background" />
                      {passwordForm.formState.errors.newPassword && (
                        <p className="text-sm bg-[#FFF0F0] text-[#872b57] rounded px-2 py-1">{passwordForm.formState.errors.newPassword.message}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword">{t('settings.confirmPassword')}</Label>
                      <Input id="confirmPassword" type="password" {...passwordForm.register('confirmPassword')} className="bg-background" />
                      {passwordForm.formState.errors.confirmPassword && (
                        <p className="text-sm bg-[#FFF0F0] text-[#872b57] rounded px-2 py-1">{passwordForm.formState.errors.confirmPassword.message}</p>
                      )}
                    </div>

                    <div className="pt-4">
                      <Button type="submit" disabled={saving}>
                        {saving ? t('settings.updating') : t('settings.updatePassword')}
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            )}
            
      {activeSection === 'appearance' && (
            <Card className="border-border bg-card text-card-foreground">
              <CardHeader>
                <CardTitle>{t('settings.appearance')}</CardTitle>
                <CardDescription>{t('settings.appearanceDesc')}</CardDescription>
              </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center justify-between border-b border-border pb-4">
                    <div className="space-y-1">
                      <Label>{t('settings.theme')}</Label>
                      <p className="text-sm text-muted-foreground">{t('settings.toggleThemeDesc', 'Toggle between light and dark mode')}</p>
                    </div>
                    <ThemeToggle />
                  </div>
                </CardContent>
              </Card>
            )}
    </div>
  );
};

export default SettingsPage;
